import {
	computed,
	ref,
	shallowRef,
	triggerRef,
	onMounted,
	onUnmounted,
	nextTick,
	type Ref,
	type ComputedRef,
} from 'vue';

// --- public types ---

export type VirtualListItem = {
	key: string;
	// tree support — items with depth/expanded enable left/right keyboard nav
	depth?: number;
	expanded?: boolean;
};

export type SelectionState = {
	getSelectedKeys: () => string[];
	setSelectedKeys: (keys: string[]) => void;
};

export type VirtualListOptions<T extends VirtualListItem> = {
	// flat list of items to render
	items: ComputedRef<T[]>;
	// fixed row height in pixels
	rowHeight: number;
	// extra rows rendered above/below viewport
	overscan?: number;
	// external selection persistence
	selectionState?: SelectionState;
	// debounce interval for persisting selection (ms)
	persistDebounce?: number;
	// custom keydown handler — return true to suppress default behavior
	onKeydown?: (event: KeyboardEvent, api: VirtualListApi<T>) => boolean;
	// called on enter or double-click
	onActivate?: (item: T) => void;
	// called when left arrow collapses an expanded item
	onCollapse?: (item: T) => void;
	// called when right arrow expands a collapsed item
	onExpand?: (item: T) => void;
};

export type VisibleEntry<T extends VirtualListItem> = T & {
	index: number;
};

export type VirtualListApi<T extends VirtualListItem> = {
	// DOM ref for the scrollable container
	containerRef: Ref<HTMLElement | null>;
	// visible entries (virtual window)
	visibleEntries: ComputedRef<VisibleEntry<T>[]>;
	topPad: ComputedRef<number>;
	bottomPad: ComputedRef<number>;
	// selection
	isSelected: (key: string) => boolean;
	isCursor: (index: number) => boolean;
	selectionRadius: (index: number) => string;
	selectOnly: (index: number) => void;
	toggleSelect: (index: number) => void;
	selectRange: (toIndex: number) => void;
	selectedItems: () => T[];
	cursorIdx: Ref<number>;
	anchorIdx: Ref<number>;
	// scrolling
	scrollIndexIntoView: (index: number) => void;
	// event handlers to bind on the container
	onKeydown: (event: KeyboardEvent) => void;
	onMousedown: (event: MouseEvent, index: number) => void;
};

export function useVirtualList<T extends VirtualListItem>(
	options: VirtualListOptions<T>,
): VirtualListApi<T> {
	const {
		items,
		rowHeight,
		overscan = 10,
		selectionState,
		persistDebounce = 100,
		onKeydown: customKeydown,
		onActivate,
		onCollapse,
		onExpand,
	} = options;

	// --- virtual scroll ---

	const containerRef = ref<HTMLElement | null>(null);
	let scrollParent: HTMLElement | null = null;
	const scrollTop = ref(0);
	const viewportHeight = ref(600);

	const visibleRange = computed(() => {
		const start = Math.max(0, Math.floor(scrollTop.value / rowHeight) - overscan);
		const visibleCount = Math.ceil(viewportHeight.value / rowHeight) + 2 * overscan;
		const end = Math.min(items.value.length, start + visibleCount);
		return { start, end };
	});

	const visibleEntries = computed(() => {
		const { start, end } = visibleRange.value;
		return items.value.slice(start, end).map((item, i) => ({
			...item,
			index: start + i,
		}));
	});

	const topPad = computed(() => visibleRange.value.start * rowHeight);
	const bottomPad = computed(() => (items.value.length - visibleRange.value.end) * rowHeight);

	function findScrollParent(el: HTMLElement | null): HTMLElement | null {
		let current = el?.parentElement ?? null;
		while (current) {
			const overflow = getComputedStyle(current).overflowY;
			if (overflow === 'auto' || overflow === 'scroll') return current;
			current = current.parentElement;
		}
		return null;
	}

	function containerOffset(): number {
		if (!scrollParent || !containerRef.value) return 0;
		const containerRect = containerRef.value.getBoundingClientRect();
		const parentRect = scrollParent.getBoundingClientRect();
		return containerRect.top - parentRect.top + scrollParent.scrollTop;
	}

	function onScroll() {
		if (!scrollParent) return;
		scrollTop.value = Math.max(0, scrollParent.scrollTop - containerOffset());
	}

	function onResize() {
		if (scrollParent) {
			viewportHeight.value = scrollParent.clientHeight;
		}
	}

	let resizeObserver: ResizeObserver | null = null;

	onMounted(async () => {
		// function refs bind after onMounted, so wait a tick for containerRef
		await nextTick();

		scrollParent = findScrollParent(containerRef.value);
		if (scrollParent) {
			viewportHeight.value = scrollParent.clientHeight;
			scrollParent.addEventListener('scroll', onScroll, { passive: true });
			resizeObserver = new ResizeObserver(onResize);
			resizeObserver.observe(scrollParent);
			onScroll();
		}

		containerRef.value?.addEventListener('keydown', handleKeydown);
	});

	onUnmounted(() => {
		if (scrollParent) {
			scrollParent.removeEventListener('scroll', onScroll);
		}
		resizeObserver?.disconnect();
		containerRef.value?.removeEventListener('keydown', handleKeydown);
		clearTimeout(persistTimer);
	});

	// --- selection state ---

	const selectedKeys = shallowRef(new Set<string>(selectionState?.getSelectedKeys() ?? []));
	const anchorIdx = ref(-1);
	const cursorIdx = ref(-1);
	const selectionVersion = ref(0);

	function markSelectionChanged() {
		selectionVersion.value++;
		triggerRef(selectedKeys);
		schedulePersist();
	}

	let persistTimer: ReturnType<typeof setTimeout> | undefined;
	function schedulePersist() {
		if (!selectionState) return;
		clearTimeout(persistTimer);
		persistTimer = setTimeout(() => {
			selectionState.setSelectedKeys([...selectedKeys.value]);
		}, persistDebounce);
	}

	function isSelected(key: string): boolean {
		void selectionVersion.value;
		return selectedKeys.value.has(key);
	}

	function isCursor(index: number): boolean {
		void selectionVersion.value;
		return cursorIdx.value === index && selectedKeys.value.size > 1;
	}

	function selectionRadius(index: number): string {
		void selectionVersion.value;
		const list = items.value;
		const sel = selectedKeys.value;
		const prevSelected = index > 0 && sel.has(list[index - 1]!.key);
		const nextSelected = index + 1 < list.length && sel.has(list[index + 1]!.key);
		if (prevSelected && nextSelected) return 'rounded-none';
		if (prevSelected) return 'rounded-t-none';
		if (nextSelected) return 'rounded-b-none';
		return '';
	}

	function selectOnly(index: number) {
		const entry = items.value[index];
		if (!entry) return;
		selectedKeys.value.clear();
		selectedKeys.value.add(entry.key);
		anchorIdx.value = index;
		cursorIdx.value = index;
		markSelectionChanged();
	}

	function toggleSelect(index: number) {
		const entry = items.value[index];
		if (!entry) return;
		if (selectedKeys.value.has(entry.key)) {
			selectedKeys.value.delete(entry.key);
		} else {
			selectedKeys.value.add(entry.key);
		}
		anchorIdx.value = index;
		cursorIdx.value = index;
		markSelectionChanged();
	}

	function selectRange(toIndex: number) {
		const list = items.value;
		const from = anchorIdx.value >= 0 && anchorIdx.value < list.length ? anchorIdx.value : -1;
		if (from === -1) {
			selectOnly(toIndex);
			return;
		}
		const start = Math.min(from, toIndex);
		const end = Math.max(from, toIndex);
		selectedKeys.value.clear();
		for (let i = start; i <= end; i++) {
			selectedKeys.value.add(list[i]!.key);
		}
		cursorIdx.value = toIndex;
		markSelectionChanged();
	}

	function selectedItems(): T[] {
		void selectionVersion.value;
		const sel = selectedKeys.value;
		return items.value.filter((item) => sel.has(item.key));
	}

	// --- scrolling ---

	function scrollIndexIntoView(index: number) {
		if (!scrollParent) return;
		const offset = containerOffset();
		const rowTop = offset + index * rowHeight;
		const rowBottom = rowTop + rowHeight;
		const viewTop = scrollParent.scrollTop;
		const viewBottom = viewTop + scrollParent.clientHeight;
		const style = getComputedStyle(scrollParent);
		const padTop = parseFloat(style.paddingTop) || 0;
		const padBottom = parseFloat(style.paddingBottom) || 0;

		if (rowTop < viewTop + padTop) {
			scrollParent.scrollTop = rowTop - padTop;
		} else if (rowBottom > viewBottom - padBottom) {
			scrollParent.scrollTop = rowBottom - scrollParent.clientHeight + padBottom;
		}
		onScroll();
	}

	// --- tree navigation helpers ---

	function itemDepth(item: T): number {
		return item.depth ?? 0;
	}

	function isExpandable(item: T): boolean {
		return item.expanded !== undefined;
	}

	function handleArrowLeft(list: T[]): boolean {
		const idx = cursorIdx.value;
		if (idx < 0 || idx >= list.length) return false;
		const entry = list[idx]!;
		const depth = itemDepth(entry);

		if (isExpandable(entry) && entry.expanded) {
			// collapse expanded item
			onCollapse?.(entry);
		} else {
			// select parent: walk backwards to first item at depth - 1
			const parentDepth = depth - 1;
			if (parentDepth < 0) return true;
			for (let i = idx - 1; i >= 0; i--) {
				if (itemDepth(list[i]!) === parentDepth && isExpandable(list[i]!)) {
					selectOnly(i);
					scrollIndexIntoView(i);
					break;
				}
			}
		}
		containerRef.value?.focus();
		return true;
	}

	function handleArrowRight(list: T[]): boolean {
		const idx = cursorIdx.value;
		if (idx < 0 || idx >= list.length) return false;
		const entry = list[idx]!;
		const depth = itemDepth(entry);

		if (isExpandable(entry)) {
			if (!entry.expanded) {
				// expand collapsed item
				onExpand?.(entry);
			} else if (idx + 1 < list.length && itemDepth(list[idx + 1]!) > depth) {
				// already expanded — select first child
				selectOnly(idx + 1);
				scrollIndexIntoView(idx + 1);
			}
		} else {
			// leaf — select next sibling (next item at same depth)
			for (let i = idx + 1; i < list.length; i++) {
				if (itemDepth(list[i]!) < depth) break;
				if (itemDepth(list[i]!) === depth) {
					selectOnly(i);
					scrollIndexIntoView(i);
					break;
				}
			}
		}
		containerRef.value?.focus();
		return true;
	}

	// --- keyboard navigation ---

	function handleKeydown(event: KeyboardEvent) {
		const list = items.value;
		if (list.length === 0) return;

		// let custom handler intercept first
		if (customKeydown?.(event, api)) return;

		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			const direction = event.key === 'ArrowDown' ? 1 : -1;
			const current = cursorIdx.value >= 0 && cursorIdx.value < list.length ? cursorIdx.value : -1;
			const nextIdx =
				current === -1 ? 0 : Math.max(0, Math.min(list.length - 1, current + direction));

			if (event.shiftKey) {
				const from =
					anchorIdx.value >= 0 && anchorIdx.value < list.length ? anchorIdx.value : nextIdx;
				const start = Math.min(from, nextIdx);
				const end = Math.max(from, nextIdx);
				selectedKeys.value.clear();
				for (let i = start; i <= end; i++) {
					selectedKeys.value.add(list[i]!.key);
				}
				cursorIdx.value = nextIdx;
				markSelectionChanged();
			} else {
				selectOnly(nextIdx);
			}

			scrollIndexIntoView(nextIdx);
			containerRef.value?.focus();
			return;
		}

		if (event.key === 'ArrowLeft') {
			event.preventDefault();
			handleArrowLeft(list);
			return;
		}

		if (event.key === 'ArrowRight') {
			event.preventDefault();
			handleArrowRight(list);
			return;
		}

		if (event.key === 'Enter') {
			const idx = cursorIdx.value;
			if (idx >= 0 && idx < list.length) {
				onActivate?.(list[idx]!);
			}
			return;
		}
	}

	// --- mouse interaction ---

	function handleMousedown(event: MouseEvent, index: number) {
		event.preventDefault();
		containerRef.value?.focus();

		// double-click activates the item
		if (event.detail === 2) {
			const item = items.value[index];
			if (item) onActivate?.(item);
			return;
		}

		if (event.shiftKey) {
			selectRange(index);
		} else if (event.ctrlKey || event.metaKey) {
			toggleSelect(index);
		} else {
			selectOnly(index);
		}
	}

	const api: VirtualListApi<T> = {
		containerRef,
		visibleEntries,
		topPad,
		bottomPad,
		isSelected,
		isCursor,
		selectionRadius,
		selectOnly,
		toggleSelect,
		selectRange,
		selectedItems,
		cursorIdx,
		anchorIdx,
		scrollIndexIntoView,
		onKeydown: handleKeydown,
		onMousedown: handleMousedown,
	};

	return api;
}
